import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  Tab,
  Tabs,
  Box,
  Typography,
  Stack,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { toast } from "react-hot-toast";
import useDownloader from "react-use-downloader";

import { RFHTextField as RHFTextField } from "../../../components/RFH";
import { CustomButton } from "../../../components/Button/Button";
import rocketChatIcon from "../../../styles/svg/rocketChat.svg";
import slackIcon from "../../../styles/svg/slack.svg";
import {
  downloadRocketChatPackage,
  updateTenantSlack,
} from "../../../services/apiSystemSettings";
import { getAPIBaseUrlVersion1 } from "../../../utils/constants";

function IntegrationsSettings({
  rocketchat_shared_secret = "",
  slack_bot_token = "",
}) {
  const [activeTab, setActiveTab] = useState("slack");
  const [isDownloadingRocketChat, setIsDownloadingRocketChat] = useState(false);
  const slackCommandUrl = `${getAPIBaseUrlVersion1()}${
    process.env.REACT_APP_SLACK_COMMAND || ""
  }${rocketchat_shared_secret || ""}`;
  const slackIntegrationUrl = `${getAPIBaseUrlVersion1()}${
    process.env.REACT_APP_INTERACTIVE_URL || ""
  }${rocketchat_shared_secret || ""}`;
  const DEFAULT_VALUES = {
    slack_command_url: slackCommandUrl,
    slack_integration_url: slackIntegrationUrl,
    slack_token: slack_bot_token || "",
  };
  const methods = useForm({
    defaultValues: DEFAULT_VALUES,
  });
  const { download } = useDownloader();
  const {
    formState: { isDirty },
  } = methods;
  const watchedSlackCommandUrl = methods.watch("slack_command_url");
  const watchedSlackIntegrationUrl = methods.watch("slack_integration_url");
  const slackToken = methods.watch("slack_token");

  useEffect(() => {
    methods.setValue("slack_command_url", slackCommandUrl);
    methods.setValue("slack_integration_url", slackIntegrationUrl);
    methods.setValue("slack_token", slack_bot_token || "");
  }, [methods, slackCommandUrl, slackIntegrationUrl, slack_bot_token]);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  const getRocketChatPackageName = useCallback((headers = {}) => {
    const contentDisposition =
      headers["content-disposition"] || headers["Content-Disposition"] || "";
    const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    const asciiMatch = contentDisposition.match(/filename="?([^"]+)"?/i);

    if (utfMatch?.[1]) {
      return decodeURIComponent(utfMatch[1]);
    }

    if (asciiMatch?.[1]) {
      return asciiMatch[1];
    }

    return "rocketchat-integration-package.zip";
  }, []);

  const handleRocketChatDownload = useCallback(async () => {
    setIsDownloadingRocketChat(true);
    toast.dismiss();

    try {
      const response = await downloadRocketChatPackage();
      const blob = response?.data;
      const packageName = getRocketChatPackageName(response?.headers);
      const url = URL.createObjectURL(blob);

      download(url, packageName);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Rocket.Chat package downloaded successfully.");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Unable to download Rocket.Chat package."
      );
    } finally {
      setIsDownloadingRocketChat(false);
    }
  }, [download, getRocketChatPackageName]);

  const onSubmit = useCallback(
    async (formData) => {
      if (activeTab === "rocketchat") {
        await handleRocketChatDownload();
      } else {
        await updateTenantSlack(formData);
        methods.reset({
          slack_command_url: formData.slack_command_url,
          slack_integration_url: formData.slack_integration_url,
          slack_token: formData.slack_token,
        });
        toast.success("Slack token updated successfully.");
      }

      return formData;
    },
    [activeTab, handleRocketChatDownload, methods]
  );

  const submitButtonLabel = useMemo(
    () => (activeTab === "rocketchat" ? "Download" : "Update"),
    [activeTab]
  );

  const handleCopy = useCallback((value) => {
    if (!value) {
      return;
    }

    navigator.clipboard.writeText(value);
    toast.success("Copied to clipboard.");
  }, []);

  const integrationTabs = useMemo(
    () => [
      {
        value: "slack",
        label: "Slack",
        icon: slackIcon,
        documentationLabel: "View Documentation",
        documentationUrl:  `${window.location.protocol}//${window.location.hostname}/TaskBoard_Slack_Integration_Tutorial.pdf`,
        setupTitle: "Slack Integration Setup",
        fields: [
          {
            name: "slack_command_url",
            label: "Slack Command URL",
            value: watchedSlackCommandUrl,
            copyable: true,
            disabled: true,
          },
          {
            name: "slack_integration_url",
            label: "Interactive URL",
            value: watchedSlackIntegrationUrl,
            copyable: true,
            disabled: true,
          },
          {
            name: "slack_token",
            label: "Token",
            value: slackToken,
            disabled: false,
          },
        ],
        steps: [
          {
            title: "Create Slack App",
            bullets: [
              "Go to Slack -> Admin Settings -> Workspace Settings -> Integrations -> Build.",
              "Click Create New App -> From Scratch, enter App Name, select workspace, and create the app.",
            ],
          },
          {
            title: "Create Slash Command",
            bullets: [
              "Open your app -> go to Slash Commands -> click Create New Command.",
              "Add your command, for example /worklog.",
              "Go to TaskBoard -> System Settings -> External Communication Setup and copy the Slack Command URL.",
              "Paste this URL into Slack command configuration and save.",
            ],
          },
          {
            title: "Enable Interactivity",
            bullets: [
              "In Slack app settings -> go to Interactivity & Shortcuts.",
              "Turn Interactivity ON.",
              "Copy Interactivity URL from TaskBoard and paste it into Slack, then save changes.",
            ],
          },
          {
            title: "Set Permissions (Scopes)",
            bullets: [
              "Go to OAuth & Permissions.",
              "Add these scopes in both sections: users:read, users:read:email, users:write.",
            ],
          },
          {
            title: "Install App to Workspace",
            bullets: [
              "Click Install App -> Install to Workspace -> Allow.",
              "After installation, copy the Bot User OAuth Token.",
            ],
          },
          {
            title: "Connect to TaskBoard",
            bullets: [
              "Go to TaskBoard -> System Settings -> External Communication Setup.",
              "Paste the Bot User OAuth Token and click Save.",
            ],
          },
        ],
      },
      {
        value: "rocketchat",
        label: "Rocket.Chat",
        icon: rocketChatIcon,
        documentationLabel: "View Documentation",
        documentationUrl: `${window.location.protocol}//${window.location.hostname}/TaskBoard_RocketChat_Integration_Tutorial.pdf`,
        setupTitle: "Rocket.Chat Setup",
        fields: [],
        steps: [
          {
            title: "Download RocketChat App",
            bullets: [
              "Click on RocketChat and download the provided ZIP file.",
            ],
          },
          {
            title: "Upload App in RocketChat",
            bullets: [
              "Open RocketChat -> 3 dots (Menu) -> Installed -> Private Apps -> Upload App.",
            ],
          },
          {
            title: "Install the App",
            bullets: [
              "Upload the downloaded ZIP file and click Install.",
            ],
          },
          {
            title: "Enable & Configure",
            bullets: [
              "Make sure the app is enabled and properly connected.",
            ],
          },
          {
            title: "Use Worklog Command",
            bullets: [
              "Now use the command in RocketChat to log work directly into TaskBoard, as configured.",
            ],
          },
        ],
      },
    ],
    [slackToken, watchedSlackCommandUrl, watchedSlackIntegrationUrl]
  );
  const activeIntegration = useMemo(
    () => integrationTabs.find((tab) => tab.value === activeTab) || integrationTabs[0],
    [activeTab, integrationTabs]
  );

  const handleDocumentationClick = useCallback(() => {

    window.open(activeIntegration.documentationUrl, "_blank", "noopener,noreferrer");
  }, [
    activeIntegration.documentationUrl,
    activeTab,
    handleRocketChatDownload,
  ]);

  return (
    <FormProvider {...methods}>
      <div className="mt-32 form-section integrations-settings">
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Typography className="setting-heading mb-4 ml-0 integrations-title">
            External Communication Setup
          </Typography>

          <div className="integrations-tabs-wrapper mb-4">
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              className="integrations-tabs"
              TabIndicatorProps={{
                style: { display: "none" },
              }}>
              {integrationTabs.map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  className={`integrations-tab ${
                    activeTab === tab.value ? "active" : ""
                  }`}
                  label={
                    <Box className="integrations-tab-label">
                      <Box
                        component="img"
                        src={tab.icon}
                        alt={`${tab.label} icon`}
                        className="integrations-tab-brand"
                      />
                      <span>{tab.label}</span>
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </div>

          <div className="integrations-layout">
            <div className="integrations-guide-card">
              <Typography className="integrations-guide-title">
                {activeIntegration.setupTitle}
              </Typography>

              <div className="integrations-guide-steps">
                {activeIntegration.steps.map((step, index) => (
                  <div className="integrations-guide-step" key={step.title}>
                    <div className="integrations-guide-marker">{index + 1}</div>
                    <div className="integrations-guide-content">
                      <Typography className="integrations-guide-step-title">
                        {step.title}
                      </Typography>
                      {step.bullets.map((bullet) => (
                        <Typography
                          key={bullet}
                          className="integrations-guide-bullet">
                          • {bullet}
                        </Typography>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="integrations-doc-link"
                onClick={handleDocumentationClick}>
                <span>{activeIntegration.documentationLabel}</span>
                <OpenInNewRoundedIcon fontSize="small" />
              </button>
            </div>

            <div className="integrations-form-panel">
              {activeIntegration.fields.length > 0 ? (
                <>
                  <Stack spacing={3}>
                    {activeIntegration.fields.map((field) => (
                      <div
                        key={field.name}
                        className={`integrations-field-row ${
                          field.copyable ? "has-copy-action" : ""
                        }`}>
                        <RHFTextField
                          name={field.name}
                          label={field.label}
                          className={`integrations-text-field ${
                            field.copyable ? "copy-enabled" : ""
                          }`}
                          disabled={field.disabled}
                        />
                        {field.copyable ? (
                          <button
                            type="button"
                            className="integrations-copy-button"
                            onClick={() => handleCopy(field.value)}
                            disabled={!field.value}>
                            <ContentCopyRoundedIcon fontSize="small" />
                            <span>Copy</span>
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </Stack>

                  <Stack alignItems="end" className="integrations-form-action">
                    <CustomButton
                      variant="contained"
                      type="submit"
                      className="theme-bg-color integrations-update-button"
                      loading={isDownloadingRocketChat}
                      disabled={!isDirty || !slackToken?.trim()}>
                      {submitButtonLabel}
                    </CustomButton>
                  </Stack>
                </>
              ) : (
                <>
                  <div className="integrations-empty-state">
                    <Typography className="integrations-empty-title">
                      Rocket.Chat package download
                    </Typography>
                    <Typography className="integrations-empty-copy">
                      Download the package, then upload it in Rocket.Chat private
                      apps to finish the setup.
                    </Typography>
                  </div>

                  <Stack alignItems="end" className="integrations-form-action">
                    <CustomButton
                      variant="contained"
                      type="button"
                      className="theme-bg-color integrations-update-button"
                      loading={isDownloadingRocketChat}
                      onClick={handleRocketChatDownload}
                      disabled={isDownloadingRocketChat}>
                      {submitButtonLabel}
                    </CustomButton>
                  </Stack>
                </>
              )}
            </div>
          </div>
        </form>
      </div>
    </FormProvider>
  );
}

export default React.memo(IntegrationsSettings);
